/**
 * FutureInterestCard — surfaces book-ahead requests captured by the AI voice.
 * Shows newest first, one-tap to mark contacted / booked / dismissed.
 */
import { useEffect, useState } from "react";
import { CalendarBlank, Phone, Check, X, ChatCircleText } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: { text: "New", cls: "bg-[#D96C5B] text-white" },
  contacted: { text: "Contacted", cls: "bg-[#F5C543] text-[#121914]" },
  booked: { text: "Placed", cls: "bg-[#1E3A2F] text-white" },
  dismissed: { text: "Dismissed", cls: "bg-[#E1DFD5] text-[#5C6B61]" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function FutureInterestCard() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const url = filter === "all" ? "/future-interest" : `/future-interest?status=${filter}`;
      const { data } = await api.get(url);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      // Backend may not be reachable — fail silently, card just stays empty.
      console.debug("future-interest load failed:", e?.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const mark = async (id, status) => {
    try {
      await api.patch(`/future-interest/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const dismiss = async (id) => {
    try {
      await api.delete(`/future-interest/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (loading || total === 0) {
    // Hide the card entirely when nothing has been captured yet — no clutter for new operators.
    return null;
  }

  return (
    <div className="paper-card p-5 mb-6" data-testid="future-interest-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-[#D96C5B]">Priority waitlist requests</div>
          <h2 className="text-xl font-heading font-extrabold tracking-tight text-[#121914] mt-1">Callers who want a head-start on the list</h2>
          <p className="text-xs text-[#5C6B61] mt-1">Captured by the AI receptionist. They're at the top of the list when service opens — not a held table. You still decide who gets seated first.</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["new", "contacted", "booked", "dismissed", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`text-xs px-2.5 py-1 rounded-full border ${filter === s ? "bg-[#1E3A2F] text-white border-[#1E3A2F]" : "border-[#E1DFD5] text-[#5C6B61] hover:bg-[#E1DFD5]"}`}
              data-testid={`fi-filter-${s}`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== "all" && counts[s] ? ` ${counts[s]}` : ""}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-[#5C6B61] py-6 text-center">No {filter === "all" ? "" : filter} requests.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#F7F6F2] border border-[#E1DFD5]"
              data-testid={`fi-row-${it.id}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A2F] flex items-center justify-center shrink-0">
                  <CalendarBlank size={20} color="#F5C543" weight="fill" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#121914]">{it.name}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_LABEL[it.status]?.cls || "bg-[#E1DFD5]"}`}>
                      {STATUS_LABEL[it.status]?.text || it.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#5C6B61] mt-0.5 truncate">
                    Party of {it.party_size} · {it.requested_datetime}
                    {it.notes && <span> · <em>{it.notes}</em></span>}
                  </div>
                  <div className="text-[10px] text-[#5C6B61] mt-0.5">
                    <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 hover:text-[#D96C5B]">
                      <Phone size={10} weight="fill" /> {it.phone}
                    </a>
                    <span className="mx-1.5">·</span>
                    {timeAgo(it.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {it.status !== "contacted" && (
                  <button
                    type="button"
                    onClick={() => mark(it.id, "contacted")}
                    title="Mark contacted"
                    className="text-xs px-2.5 py-1.5 rounded-full border border-[#E1DFD5] text-[#5C6B61] hover:bg-[#F5C543]/20 inline-flex items-center gap-1"
                    data-testid={`fi-contacted-${it.id}`}
                  >
                    <ChatCircleText size={12} weight="fill" /> Contacted
                  </button>
                )}
                {it.status !== "booked" && (
                  <button
                    type="button"
                    onClick={() => mark(it.id, "booked")}
                    title="Mark placed on the list"
                    className="text-xs px-2.5 py-1.5 rounded-full bg-[#1E3A2F] text-white hover:bg-[#2b5648] inline-flex items-center gap-1"
                    data-testid={`fi-booked-${it.id}`}
                  >
                    <Check size={12} weight="bold" /> Placed
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => dismiss(it.id)}
                  title="Delete"
                  className="text-xs px-2 py-1.5 rounded-full text-[#5C6B61] hover:bg-[#D96C5B]/10 hover:text-[#D96C5B]"
                  data-testid={`fi-delete-${it.id}`}
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
